import React from "react";

import { isCancel } from "axios";

import { getPreviews } from "@synthesis/shared-services/filesystem-gge/pdfDocument/client";
import type { PdfPreviewResponse } from "@synthesis/shared-services/filesystem-gge/pdfDocument/types";

import { type PagePreview, getPreviewImageSrc, mapPdfPreviewsResponse } from "../components/modal-open-pdf.lib";

const PREVIEW_BATCH_SIZE = 10;

type PdfPreviewItem = NonNullable<PdfPreviewResponse["previews"]>[number];

type SidebarPreviewsState = {
  items: PagePreview[];
  totalPageCount: number;
  isInitialLoading: boolean;
};

const INITIAL_STATE: SidebarPreviewsState = {
  items: [],
  totalPageCount: 0,
  isInitialLoading: false,
};

const isValidPreviewItem = (item: PdfPreviewItem): item is { page: number; content: string } => item.page != null && !!item.content;

const mapPreviews = (items: PdfPreviewItem[]): PagePreview[] =>
  items.filter(isValidPreviewItem).map((item) => ({
    page: item.page,
    img: getPreviewImageSrc(item.content),
  }));

const buildPlaceholderPreviews = (pageCount: number): PagePreview[] =>
  Array.from({ length: pageCount }, (_, index) => ({
    page: index + 1,
  }));

const chunkPages = (pageCount: number) => {
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1);
  const chunks: number[][] = [];

  for (let index = 0; index < pages.length; index += PREVIEW_BATCH_SIZE) {
    chunks.push(pages.slice(index, index + PREVIEW_BATCH_SIZE));
  }

  return chunks;
};

const prioritizeEdgeBatches = (batches: number[][]) => {
  if (batches.length <= 2) {
    return batches;
  }

  return [batches[0], batches[batches.length - 1], ...batches.slice(1, -1)];
};

const mergePreviews = (currentItems: PagePreview[], loadedItems: PagePreview[]) => {
  const previewsByPage = new Map(currentItems.map((item) => [item.page, item]));

  loadedItems.forEach((item) => {
    previewsByPage.set(item.page, item);
  });

  return Array.from(previewsByPage.values()).toSorted((a, b) => a.page - b.page);
};

export const usePdfSidebarPreviews = (fileId: string | undefined, isOpen: boolean, pageCount = 0) => {
  const [state, setState] = React.useState<SidebarPreviewsState>(INITIAL_STATE);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen || !fileId) {
      setState(INITIAL_STATE);
      return;
    }

    let cancelled = false;
    const abortControllers: AbortController[] = [];
    const initialPageCount = Math.max(0, pageCount);

    (async () => {
      setState({
        items: buildPlaceholderPreviews(initialPageCount),
        totalPageCount: initialPageCount,
        isInitialLoading: true,
      });

      try {
        let effectivePageCount = initialPageCount;
        let initialPreviewItems: PagePreview[] = [];

        if (effectivePageCount === 0) {
          const initialAbort = new AbortController();
          abortControllers.push(initialAbort);
          const data = (await getPreviews({ path: { fileId }, body: { pages: [1] }, signal: initialAbort.signal })).data;

          if (cancelled) {
            return;
          }

          const { pageCount: loadedPageCount } = mapPdfPreviewsResponse(data);
          effectivePageCount = loadedPageCount > 0 ? loadedPageCount : 1;
          initialPreviewItems = mapPreviews(data.previews ?? []);

          setState({
            items: mergePreviews(buildPlaceholderPreviews(effectivePageCount), initialPreviewItems),
            totalPageCount: effectivePageCount,
            isInitialLoading: true,
          });
        }

        const batches = prioritizeEdgeBatches(chunkPages(effectivePageCount));

        await Promise.all(
          batches.map(async (pages) => {
            const abortController = new AbortController();
            abortControllers.push(abortController);

            const response = (await getPreviews({ path: { fileId }, body: { pages }, signal: abortController.signal })).data;

            if (!cancelled) {
              const loadedPreviews = mapPreviews(response.previews ?? []);
              setState((prev) => ({
                ...prev,
                items: mergePreviews(prev.items, loadedPreviews),
              }));
            }

            return response;
          }),
        );

        if (cancelled) {
          return;
        }

        setState((prev) => ({
          ...prev,
          isInitialLoading: false,
        }));
      } catch (err) {
        if (!cancelled) {
          if (!isCancel(err)) {
            console.error("Error loading PDF previews:", err);
          }
          setState(INITIAL_STATE);
        }
      }
    })();

    return () => {
      cancelled = true;
      abortControllers.forEach((controller) => controller.abort());
    };
  }, [fileId, isOpen, pageCount]);

  return {
    pagePreviews: state.items,
    isLoadingPreviews: state.isInitialLoading,
    sidebarRef,
  };
};
